import { Filter, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { type FilterType } from "../api";

/** A filter resolved for rendering: an author-defined FilterDef merged with (or
 *  falling back to) an auto-detected {{param}} token. */
export interface ResolvedFilter {
  param: string;
  label: string;
  type: FilterType;
  options?: string[];
  /** When set, this filter auto-filters result rows on that column client-side
   *  (in addition to any {{param}} substitution). */
  column?: string;
}

/**
 * The dashboard filter bar (custom — not an AG feature). Renders one input per
 * filter; changing a value re-runs every panel whose SQL references that
 * `{{param}}` (substituted server-side by the query executor). Typed defs get a
 * label + the right control (date picker / select); undefined params fall back
 * to text.
 */
export function FilterBar({
  filters,
  values,
  onChange,
}: {
  filters: ResolvedFilter[];
  values: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
}) {
  if (filters.length === 0) return null;

  const set = (param: string, value: string) => onChange({ ...values, [param]: value });
  const clear = () => onChange({});
  const hasValues = filters.some((f) => values[f.param]);

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-lg border border-border bg-card p-3">
      <div className="flex items-center gap-1.5 self-center text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        <Filter className="h-3.5 w-3.5" />
        Filters
      </div>
      {filters.map((f) => (
        <div key={f.param} className="space-y-1">
          <span className="block text-[11px] font-medium text-muted-foreground">{f.label}</span>
          {f.type === "select" ? (
            <Select value={values[f.param] ?? ""} onValueChange={(v) => set(f.param, v)}>
              <SelectTrigger className="h-8 w-40">
                <SelectValue placeholder="(any)" />
              </SelectTrigger>
              <SelectContent>
                {(f.options ?? []).map((o) => (
                  <SelectItem key={o} value={o}>
                    {o}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <Input
              type={f.type === "date" ? "date" : "text"}
              value={values[f.param] ?? ""}
              onChange={(e) => set(f.param, e.target.value)}
              placeholder={f.param}
              className="h-8 w-40"
            />
          )}
        </div>
      ))}
      {hasValues && (
        <Button variant="ghost" size="sm" onClick={clear} className="self-center">
          <X className="h-3.5 w-3.5" />
          Clear
        </Button>
      )}
    </div>
  );
}
